import { useTranslations } from 'use-intl'
import { toast } from 'sonner'
import { 
  MapPin, 
  Copy, 
  Train, 
  Clock, 
  Banknote, 
  Users, 
  DoorClosed, 
  Car, 
  Baby, 
  Cigarette, 
  CigaretteOff, 
  Smartphone,
  CreditCard,
  QrCode,
  Wallet
} from 'lucide-react'

interface ShopInfoSectionProps {
  address: string | null
  access: string | null
  openHours: string | null
  lunchBudgetMin: number | null
  lunchBudgetMax: number | null
  dinnerBudgetMin: number | null
  dinnerBudgetMax: number | null
  budgetString: string | null
  capacity: number | null
  hasPrivateRoom: boolean | null
  hasParking: boolean | null
  kidsAllowed: boolean | null
  smokingStatus: string | null // 'Non-smoking', 'Separated', 'Smoking allowed' etc
  hasOnlineBooking: boolean | null
  acceptsCreditCard: boolean | null
  acceptsElectronicMoney: boolean | null
  acceptsQrCode: boolean | null
}

function InfoRow({ icon: Icon, label, children }: { icon: any, label: string, children: React.ReactNode }) {
  return (
    <div className="flex gap-4 p-4 rounded-xl hover:bg-surface-container transition-colors">
      <div className="text-primary mt-1">
        <Icon size={20} />
      </div>
      <div className="flex-1 flex flex-col gap-1">
        <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant/70">
          {label}
        </span>
        <div className="text-sm font-medium text-on-surface">
          {children}
        </div>
      </div>
    </div>
  )
}

function Chip({ icon: Icon, label }: { icon: any, label: string }) {
  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-outline-variant/15 bg-surface-container-low text-xs font-medium text-on-surface">
      <Icon size={14} className="text-primary" />
      {label}
    </div>
  )
}

export function ShopInfoSection(props: ShopInfoSectionProps) {
  const t = useTranslations('shopDetail')

  const copyAddress = () => {
    if (props.address) {
      navigator.clipboard.writeText(props.address)
      toast.success(t('addressCopied'))
    }
  }

  // Determine budget display
  const hasLunchParams = props.lunchBudgetMin !== null || props.lunchBudgetMax !== null
  const hasDinnerParams = props.dinnerBudgetMin !== null || props.dinnerBudgetMax !== null
  const hasBudget = hasLunchParams || hasDinnerParams || props.budgetString

  // Collect facilities
  const facilities = []
  if (props.hasPrivateRoom) facilities.push({ icon: DoorClosed, label: t('privateRoom') })
  if (props.hasParking) facilities.push({ icon: Car, label: t('parking') })
  if (props.kidsAllowed) facilities.push({ icon: Baby, label: t('kidsAllowed') })
  if (props.hasOnlineBooking) facilities.push({ icon: Smartphone, label: t('onlineBooking') })
  
  if (props.smokingStatus) {
    const isNonSmoking = props.smokingStatus.toLowerCase().includes('non-smoking')
    const isSeparated = props.smokingStatus.toLowerCase().includes('separated')
    facilities.push({ 
      icon: isNonSmoking ? CigaretteOff : Cigarette, 
      label: isNonSmoking ? t('smokingNonSmoking') : isSeparated ? t('smokingSeparated') : t('smokingAllowed')
    })
  }

  // Collect payments
  const payments = []
  if (props.acceptsCreditCard) payments.push({ icon: CreditCard, label: t('creditCard') })
  if (props.acceptsElectronicMoney) payments.push({ icon: Wallet, label: t('electronicMoney') })
  if (props.acceptsQrCode) payments.push({ icon: QrCode, label: t('qrCode') })

  return (
    <section className="w-full pb-8">
      <div className="bg-surface-container-low p-6 md:p-8 rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.03)] flex flex-col gap-2">
        
        {props.address && (
          <InfoRow icon={MapPin} label={t('address')}>
            <div className="flex items-start justify-between gap-4">
              <span>{props.address}</span>
              <button 
                onClick={copyAddress}
                className="text-on-surface-variant hover:text-primary transition-colors p-2 -mr-2 -mt-2 rounded-full hover:bg-surface-container-high active:scale-95"
                title={t('copyAddress')}
              >
                <Copy size={16} />
              </button>
            </div>
          </InfoRow>
        )}

        {props.access && (
          <InfoRow icon={Train} label={t('access')}>
            {props.access}
          </InfoRow>
        )}

        {props.openHours && (
          <InfoRow icon={Clock} label={t('businessHours')}>
            <div className="whitespace-pre-wrap">{props.openHours}</div>
          </InfoRow>
        )}

        {hasBudget && (
          <InfoRow icon={Banknote} label="Budget">
            <div className="flex flex-col gap-1">
              {hasLunchParams && (
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 rounded bg-surface-variant text-on-surface-variant">{t('lunchBudget')}</span>
                  <span>
                    ¥{props.lunchBudgetMin?.toLocaleString() || 0} ~ ¥{props.lunchBudgetMax?.toLocaleString() || '---'}
                  </span>
                </div>
              )}
              {hasDinnerParams && (
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 rounded bg-surface-variant text-on-surface-variant">{t('dinnerBudget')}</span>
                  <span>
                    ¥{props.dinnerBudgetMin?.toLocaleString() || 0} ~ ¥{props.dinnerBudgetMax?.toLocaleString() || '---'}
                  </span>
                </div>
              )}
              {!hasLunchParams && !hasDinnerParams && props.budgetString && (
                <span>{props.budgetString}</span>
              )}
            </div>
          </InfoRow>
        )}

        {props.capacity && (
          <InfoRow icon={Users} label={t('capacity')}>
            {props.capacity} {t('capacityUnit')}
          </InfoRow>
        )}

        {facilities.length > 0 && (
          <InfoRow icon={DoorClosed} label={t('facilities')}>
            <div className="flex flex-wrap gap-2 mt-1">
              {facilities.map((f, i) => <Chip key={i} icon={f.icon} label={f.label} />)}
            </div>
          </InfoRow>
        )}

        {payments.length > 0 && (
          <InfoRow icon={CreditCard} label={t('payment')}>
            <div className="flex flex-wrap gap-2 mt-1">
              {payments.map((p, i) => <Chip key={i} icon={p.icon} label={p.label} />)}
            </div>
          </InfoRow>
        )}

      </div>
    </section>
  )
}
